import { PermissionsAndroid, Platform } from 'react-native';
import {
  iosLocationModule,
  subscribeIosLocation,
  type IosLocationStatus,
  type NativeFix,
} from '../native/LocationTrackingService';

/** denied: may be asked again. blocked: only Settings can change it. disabled: location services off. */
export type PermissionResult = 'granted' | 'denied' | 'blocked' | 'disabled';

export type RawPosition = {
  coords: {
    latitude: number;
    longitude: number;
    accuracy?: number | null;
    altitude?: number | null;
    speed?: number | null;
    heading?: number | null;
  };
  timestamp: number;
};

/** Same codes as the geolocation API: 1 permission denied, 2 unavailable, 3 timeout. */
export type GeoError = { code: number; message?: string };

export type WatchOptions = {
  intervalMs: number;
  fastestIntervalMs: number;
  highAccuracy: boolean;
};

export type WatchHandle = { stop: () => void; background: boolean };

export type LocationSource = {
  requestPermission: () => Promise<PermissionResult>;
  watch: (
    onFix: (position: RawPosition) => void,
    onError: (error: GeoError) => void,
    options: WatchOptions,
  ) => Promise<WatchHandle>;
  currentPosition: (options: {
    timeoutMs: number;
    maximumAgeMs: number;
    highAccuracy: boolean;
  }) => Promise<RawPosition>;
};

type Geolocation = {
  watchPosition: (
    success: (position: RawPosition) => void,
    error: (error: GeoError) => void,
    options: Record<string, number | boolean>,
  ) => number;
  clearWatch: (watchId: number) => void;
  getCurrentPosition: (
    success: (position: RawPosition) => void,
    error: (error: GeoError) => void,
    options: Record<string, number | boolean>,
  ) => void;
  setRNConfiguration: (config: Record<string, unknown>) => void;
};

export class LocationTimeoutError extends Error {
  readonly code = 3;
  constructor(message = 'Location request timed out.') {
    super(message);
  }
}

/** Never lets a native call keep the caller waiting longer than ms. */
export function withTimeout<T>(promise: Promise<T>, ms: number, message?: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new LocationTimeoutError(message)), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function loadGeolocation(): Geolocation {
  // Load the native module only when location is used.
  return require('@react-native-community/geolocation').default;
}

function libraryPosition(
  geolocation: Geolocation,
  options: Record<string, number | boolean>,
): Promise<RawPosition> {
  return new Promise((resolve, reject) =>
    geolocation.getCurrentPosition(resolve, reject, options),
  );
}

/** First successful fix of several requests; rejects with the first failure if none succeed. */
function firstFix(attempts: Promise<RawPosition>[]): Promise<RawPosition> {
  return new Promise((resolve, reject) => {
    let failed = 0;
    let firstError: unknown = null;
    attempts.forEach(attempt =>
      attempt.then(resolve, error => {
        firstError ??= error;
        failed += 1;
        if (failed === attempts.length) reject(firstError);
      }),
    );
  });
}

function fromNative(fix: NativeFix): RawPosition {
  return {
    coords: {
      latitude: fix.latitude,
      longitude: fix.longitude,
      accuracy: fix.accuracy,
      altitude: fix.altitude,
      speed: fix.speed,
      heading: fix.heading,
    },
    timestamp: fix.timestamp,
  };
}

function iosPermission(status: IosLocationStatus): PermissionResult {
  if (!status.servicesEnabled) return 'disabled';
  if (status.status === 'whenInUse' || status.status === 'always') return 'granted';
  if (status.status === 'notDetermined') return 'denied';
  return 'blocked';
}

function nativeError(error: unknown): GeoError {
  const code = (error as { code?: string })?.code;
  if (code === 'PERMISSION_DENIED') return { code: 1, message: 'Location permission denied.' };
  if (code === 'LOCATION_DISABLED') return { code: 2, message: 'Location services disabled.' };
  if (error instanceof LocationTimeoutError) return { code: 3, message: error.message };
  return { code: 2, message: (error as Error)?.message };
}

/** iOS: dedicated CLLocationManager with background updates (ios/arkhrms/LocationTrackingService.m). */
function iosNativeSource(native: NonNullable<ReturnType<typeof iosLocationModule>>): LocationSource {
  return {
    async requestPermission() {
      return iosPermission(await native.requestPermission());
    },
    async watch(onFix, onError, options) {
      const unsubscribe = subscribeIosLocation(fix => onFix(fromNative(fix)), onError);
      try {
        const started = await native.start({ highAccuracy: options.highAccuracy, background: true });
        return {
          background: started.background,
          stop: () => {
            unsubscribe();
            void native.stop().catch(() => undefined);
          },
        };
      } catch (error) {
        unsubscribe();
        throw nativeError(error);
      }
    },
    async currentPosition({ timeoutMs, maximumAgeMs }) {
      try {
        return fromNative(
          await withTimeout(native.getCurrentPosition({ maximumAge: maximumAgeMs }), timeoutMs),
        );
      } catch (error) {
        throw nativeError(error);
      }
    },
  };
}

async function androidPermission(): Promise<PermissionResult> {
  const { ACCESS_FINE_LOCATION, ACCESS_COARSE_LOCATION, POST_NOTIFICATIONS } =
    PermissionsAndroid.PERMISSIONS;
  const { GRANTED, NEVER_ASK_AGAIN } = PermissionsAndroid.RESULTS;
  const results = await PermissionsAndroid.requestMultiple([
    ACCESS_FINE_LOCATION,
    ACCESS_COARSE_LOCATION,
  ]);
  if (Number(Platform.Version) >= 33 && POST_NOTIFICATIONS) {
    // Shows the tracking notification; tracking still works if refused.
    await PermissionsAndroid.request(POST_NOTIFICATIONS).catch(() => undefined);
  }
  if (results[ACCESS_FINE_LOCATION] === GRANTED || results[ACCESS_COARSE_LOCATION] === GRANTED) {
    return 'granted';
  }
  return results[ACCESS_FINE_LOCATION] === NEVER_ASK_AGAIN ||
    results[ACCESS_COARSE_LOCATION] === NEVER_ASK_AGAIN
    ? 'blocked'
    : 'denied';
}

/**
 * Android: Google's fused provider while tracking. It honours the interval and
 * uses Wi-Fi/cell indoors. Without Play Services the library keeps the platform
 * provider, which works but reports every second (JS keeps one per interval).
 */
function androidSource(): LocationSource {
  return {
    requestPermission: androidPermission,
    async watch(onFix, onError, options) {
      const geolocation = loadGeolocation();
      geolocation.setRNConfiguration({ skipPermissionRequests: true, locationProvider: 'playServices' });
      const watchId = geolocation.watchPosition(onFix, onError, {
        enableHighAccuracy: options.highAccuracy,
        interval: options.intervalMs,
        fastestInterval: options.fastestIntervalMs,
        distanceFilter: 0,
        maximumAge: options.fastestIntervalMs,
        timeout: options.intervalMs,
      });
      return {
        background: false,
        stop: () => {
          geolocation.clearWatch(watchId);
          // Restore the library defaults the rest of the app was built against.
          geolocation.setRNConfiguration({ skipPermissionRequests: false, locationProvider: 'android' });
        },
      };
    },
    currentPosition({ timeoutMs, maximumAgeMs, highAccuracy }) {
      const geolocation = loadGeolocation();
      // GPS rarely fixes indoors; the network provider answers there. Keep whichever arrives first.
      const attempts = [
        libraryPosition(geolocation, { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: maximumAgeMs }),
      ];
      if (highAccuracy) {
        attempts.push(
          libraryPosition(geolocation, { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: maximumAgeMs }),
        );
      }
      return withTimeout(firstFix(attempts), timeoutMs + 1000);
    },
  };
}

/** Web, and iOS binaries built before the native module: the geolocation library only. */
function librarySource(platform: string): LocationSource {
  return {
    // The first location request prompts; a refusal arrives as error code 1.
    requestPermission: async () => 'granted',
    async watch(onFix, onError, options) {
      const geolocation = loadGeolocation();
      if (platform === 'ios') {
        geolocation.setRNConfiguration({
          skipPermissionRequests: false,
          authorizationLevel: 'whenInUse',
          enableBackgroundLocationUpdates: false,
        });
      }
      const watchId = geolocation.watchPosition(onFix, onError, {
        enableHighAccuracy: options.highAccuracy,
        distanceFilter: 0,
        maximumAge: options.fastestIntervalMs,
        timeout: options.intervalMs,
      });
      return { background: false, stop: () => geolocation.clearWatch(watchId) };
    },
    currentPosition({ timeoutMs, maximumAgeMs, highAccuracy }) {
      return withTimeout(
        libraryPosition(loadGeolocation(), {
          enableHighAccuracy: highAccuracy,
          timeout: timeoutMs,
          maximumAge: maximumAgeMs,
        }),
        timeoutMs + 1000,
      );
    },
  };
}

let source: LocationSource | null = null;

export function getLocationSource(): LocationSource {
  if (!source) {
    const ios = iosLocationModule();
    source =
      Platform.OS === 'android'
        ? androidSource()
        : ios
          ? iosNativeSource(ios)
          : librarySource(Platform.OS);
  }
  return source;
}
